import { useScrollReveal, getRevealStyle } from '@/hooks/useScrollReveal';

const directionCategories = [
  {
    title: '自然科學',
    icon: 'ri-leaf-line',
  },
  {
    title: '動植物',
    icon: 'ri-seedling-line',
  },
  {
    title: '地球與環境',
    icon: 'ri-earth-line',
  },
  {
    title: '宇宙與天文',
    icon: 'ri-moon-line',
  },
  {
    title: '人體與健康知識',
    icon: 'ri-heart-pulse-line',
  },
  {
    title: '科技知識',
    icon: 'ri-cpu-line',
  },
  {
    title: '世界知識',
    icon: 'ri-global-line',
  },
  {
    title: '生活科學',
    icon: 'ri-lightbulb-line',
  },
];

export default function PublishingDirectionSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.08 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-100 border-t border-primary-500/8"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        {/* Section label */}
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            Direction
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            03
          </span>
        </div>

        {/* Main heading */}
        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-100 leading-[1.1] tracking-tight text-center text-glow"
        >
          主要出版方向
        </h2>

        {/* Primary tagline */}
        <p
          style={getRevealStyle(isVisible, 80)}
          className="mt-5 text-sm md:text-base text-primary-400 leading-[1.7] text-center font-label tracking-wide"
        >
          科普類書籍
        </p>

        <div
          style={getRevealStyle(isVisible, 100)}
          className="mt-6 w-12 h-0.5 bg-primary-500 mx-auto shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/40"
        />

        {/* Description */}
        <p
          style={getRevealStyle(isVisible, 120)}
          className="mt-8 text-sm md:text-base text-foreground-300 leading-[1.9] max-w-[720px] mx-auto text-center"
        >
          機曜文化聚焦科普類書籍，透過出版整理自然、科學、科技與生活知識，提供兼具知識理解與閱讀價值的內容。
        </p>

        {/* Direction categories grid */}
        <div
          style={getRevealStyle(isVisible, 160)}
          className="mt-12 md:mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-[880px] mx-auto"
        >
          {directionCategories.map((cat) => (
            <div
              key={cat.title}
              className="relative border border-primary-500/10 bg-background-50/40 px-4 py-4 group hover:border-primary-500/25 transition-all duration-300 cursor-default"
            >
              <span className="absolute top-0 left-0 w-2.5 h-px bg-primary-500/25 group-hover:bg-primary-500/45 transition-colors" />
              <span className="absolute top-0 left-0 w-px h-2.5 bg-primary-500/25 group-hover:bg-primary-500/45 transition-colors" />

              <div className="flex items-center gap-2.5">
                <span className="w-5 h-5 flex items-center justify-center text-primary-400">
                  <i className={`${cat.icon} text-sm`} />
                </span>
                <span className="text-sm font-medium text-foreground-200 leading-snug">
                  {cat.title}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Context note */}
        <div
          style={getRevealStyle(isVisible, 200)}
          className="mt-10 md:mt-14 max-w-[720px] mx-auto"
        >
          <div className="border border-primary-500/10 bg-background-50/40 px-6 py-5 md:px-8 md:py-6">
            <p className="text-xs text-foreground-600 leading-[1.8]">
              科普是機曜文化的出版方向，相關內容屬於出版與一般知識閱讀。機曜文化是出版品牌，不代表為科學研究機構、教育機構、醫療單位、科技顧問公司或專業認證機構。
            </p>
          </div>
        </div>

        {/* Divider */}
        <div
          style={getRevealStyle(isVisible, 240)}
          className="mt-14 md:mt-20 flex items-center justify-center gap-4"
        >
          <div className="w-16 h-px bg-primary-500/15" />
          <span className="text-[10px] font-medium text-foreground-700 tracking-[0.25em] uppercase font-label">
            書目查詢
          </span>
          <div className="w-16 h-px bg-primary-500/15" />
        </div>

        {/* Catalog entry card */}
        <div
          style={getRevealStyle(isVisible, 260)}
          className="mt-8 max-w-[620px] mx-auto"
        >
          <div className="relative border border-primary-500/15 bg-background-50/40 p-8 md:p-10 text-center">
            {/* Corner accents */}
            <span className="absolute top-0 left-0 w-8 h-px bg-primary-500/30" />
            <span className="absolute top-0 left-0 w-px h-8 bg-primary-500/30" />
            <span className="absolute bottom-0 right-0 w-8 h-px bg-primary-500/30" />
            <span className="absolute bottom-0 right-0 w-px h-8 bg-primary-500/30" />

            <div className="w-12 h-12 mx-auto flex items-center justify-center bg-primary-500/10 border border-primary-500/20 text-primary-400">
              <i className="ri-database-2-line text-base" />
            </div>
            <p className="mt-5 text-xs text-primary-400 tracking-widest uppercase font-label">
              崧博出版 · 書籍資料庫
            </p>
            <h3 className="mt-2 font-heading text-lg font-bold text-foreground-100">
              catalog.sonbook.net
            </h3>
            <p className="mt-4 text-sm text-foreground-400 leading-[1.9]">
              完整書名、作者、內容主題及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
            </p>
            <div className="mt-6">
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15"
              >
                前往書籍資料庫
                <i className="ri-arrow-right-up-line text-xs" />
              </a>
            </div>
          </div>

          {/* Sonbook info */}
          <div className="mt-4 border border-primary-500/10 bg-background-50/40 px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-primary-400 tracking-widest uppercase font-label">
              崧博出版官網 · sonbook.net
            </p>
            <a
              href="https://sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-1 text-sm text-foreground-400 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap cursor-pointer border-b border-primary-500/30 pb-0.5"
            >
              認識崧博出版
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}